import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

/** An inline affordance on the toast — an "Undo" for a just-applied bulk action. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  exiting: boolean;
  action?: ToastAction;
}

interface ToastContextValue {
  addToast: (message: string, type: ToastType, duration?: number, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const TOAST_ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />,
  error: <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />,
  warning: <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />,
  info: <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />,
};

const TOAST_STYLES: Record<ToastType, string> = {
  success: 'border-green-500/30 bg-green-500/10',
  error: 'border-red-500/30 bg-red-500/10',
  warning: 'border-yellow-500/30 bg-yellow-500/10',
  info: 'border-blue-500/30 bg-blue-500/10',
};

const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 4000;

const ToastItem: React.FC<{
  toast: Toast;
  onClose: (id: string) => void;
}> = ({ toast, onClose }) => {
  return (
    <div
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`
        flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm
        bg-wiz-card ${TOAST_STYLES[toast.type]}
        transition-all duration-300 ease-in-out
        ${toast.exiting
          ? 'opacity-0 translate-x-full'
          : 'opacity-100 translate-x-0'
        }
      `}
      style={{ minWidth: '320px', maxWidth: '420px' }}
    >
      {TOAST_ICONS[toast.type]}
      <p className="text-sm text-gray-200 flex-1 break-words whitespace-pre-wrap">{toast.message}</p>
      {toast.action && (
        <button
          onClick={() => { toast.action!.onClick(); onClose(toast.id); }}
          className="text-xs font-semibold text-wiz-purple hover:brightness-125 underline underline-offset-2 flex-shrink-0"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => onClose(toast.id)}
        className="text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const removeToast = useCallback((id: string) => {
    // Clear any existing timer for this toast
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    // Mark as exiting for animation
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));

    // Remove after animation completes
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback((message: string, type: ToastType, duration: number = DEFAULT_DURATION, action?: ToastAction) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = { id, message, type, duration, exiting: false, action };

    setToasts(prev => {
      // Dedupe: if the same message is already showing (not exiting), skip it.
      // During a live scan many collectors emit the same finding text; without
      // this a single event toasted by more than one subscriber stacks twice.
      // An actionable toast is exempt — two bulk actions each need their own Undo.
      if (!action && prev.some(t => !t.exiting && t.message === message)) {
        return prev;
      }
      const updated = [...prev, newToast];
      // Keep only the most recent MAX_VISIBLE toasts
      if (updated.length > MAX_VISIBLE) {
        const excess = updated.slice(0, updated.length - MAX_VISIBLE);
        excess.forEach(t => {
          const timer = timersRef.current.get(t.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(t.id);
          }
        });
        return updated.slice(-MAX_VISIBLE);
      }
      return updated;
    });

    // Set auto-dismiss timer
    const timer = setTimeout(() => {
      removeToast(id);
    }, duration);
    timersRef.current.set(id, timer);
  }, [removeToast]);

  // Cleanup all timers on unmount
  useEffect(() => {
    const currentTimers = timersRef.current;
    return () => {
      currentTimers.forEach(timer => clearTimeout(timer));
      currentTimers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {/* Toast container - fixed bottom-right */}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-2 pointer-events-none"
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onClose={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
